import { createContext, useContext, useEffect, useState } from 'react'

// EXPANDED MAPS ARE CHROMELESS, LIKE GO MODE (Randy, 2026-09-17).
//
// "Expanded maps are chromeless like Go Mode: while ANY map is maximized
// (home/explore, station panel, chart expandInPlace) the app header is hidden
// and the control column sits at CONTROL_INSET from the top, right 12, on
// every surface."
//
// Go Mode already had this and got it from `_Layout`'s CHROMELESS_PAGES, which
// is keyed by the Inertia page name — a page either is Go Mode or is not. A
// maximized map is not a page: it is a piece of state that lives inside
// ExploreMap, changes without a navigation, and exists on four surfaces that
// are all different pages. So the switch the layout reads has to be something
// the map can WRITE, which is this module.
//
// TWO CONTEXTS, NOT ONE, and the split is the point. The state context is read
// by the shell, which must re-render when a map maximizes. The setter context
// is read by the map, which must NOT: `useState`'s setter is referentially
// stable, so this context's value never changes and a maximize never re-renders
// the map through the provider it just wrote to. One combined `{ value, set }`
// object would have re-rendered ExploreMap — and the whole maplibre subtree
// under it — on every toggle, for nothing.
const ChromelessStateContext = createContext(null)
const ChromelessSetContext = createContext(null)

export function ChromelessProvider({ children }) {
  // `null` means NO MAP HAS REPORTED YET — the server render, and the window
  // before the map chunk lands. It is not the same as `false`, because the
  // shell has a second source for that window (see useChromeless below), and
  // the difference is a header that paints for a second on `/explore` and then
  // vanishes. `false` means a map is mounted and is not maximized.
  const [mapMaximized, setMapMaximized] = useState(null)
  return (
    <ChromelessSetContext.Provider value={setMapMaximized}>
      <ChromelessStateContext.Provider value={mapMaximized}>
        {children}
      </ChromelessStateContext.Provider>
    </ChromelessSetContext.Provider>
  )
}

// The shell's question: is a map maximized right now? `null` while nothing has
// said, so the caller can fall back to what the server already knows.
export function useChromeless() {
  return useContext(ChromelessStateContext)
}

// The map's half. Publishes `active` for as long as this component is mounted,
// and hands the answer back on unmount — a map that is gone is not a maximized
// map, and a crashed one (LazyMap's fallback panel) must not leave the reader
// on a headerless error page.
export function useChromelessWhile(active) {
  const setMapMaximized = useContext(ChromelessSetContext)
  useEffect(() => {
    if (!setMapMaximized) return undefined
    setMapMaximized(active)
    return () => setMapMaximized(false)
  }, [active, setMapMaximized])
}
